/**
 * 全局网络错误共享状态（Pinia Store）
 *
 * 用途：当后端服务不可达时，在 App.vue 顶部展示统一的系统异常横幅。
 *
 * 工作流程：
 *   httpClient 拦截器捕获到网络/超时/服务端错误 → 派发 globalError 事件
 *   本 store 监听事件 → 更新 globalError ref
 *   App.vue 中的 SystemAlertBanner 消费 globalError → 展示降级 UI
 *
 * 错误在以下情况自动清除：
 *   - 用户点击"我知道了"
 *   - 下一个成功的 API 请求自动清除
 *   - 路由切换时清除（避免跨页面残留）
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { ErrorClassify } from '@/utils/httpClient'

export interface GlobalSystemError {
  /** 错误分类 */
  category: ErrorClassify
  /** 用户可见消息 */
  message: string
  /** 发生时间戳 */
  timestamp: number
  /** HTTP 状态码（如有） */
  status?: number
  /** 业务错误码（如有） */
  errorCode?: number
}

export const useSystemErrorStore = defineStore('systemError', () => {
  /** 当前全局系统错误；null 表示正常 */
  const globalError = ref<GlobalSystemError | null>(null)

  const hasError = computed(() => globalError.value !== null)

  const errorCategory = computed(() => globalError.value?.category ?? 'unknown')

  /** 清屏全局错误 */
  function clear() {
    globalError.value = null
  }

  /** 设置全局系统错误 */
  function setError(payload: Omit<GlobalSystemError, 'timestamp'>) {
    globalError.value = { ...payload, timestamp: Date.now() }
  }

  return { globalError, hasError, errorCategory, clear, setError }
})
